/********************************************************************************
 * Copyright (c) 2026 CrossBreeze, EclipseSource and others.
 *
 * This program and the accompanying materials are made available under the
 * terms of the MIT License which is available in the project root.
 *
 * SPDX-License-Identifier: MIT
 ********************************************************************************/

/**
 * Subpath barrel for `@hydranium/protocol/client` — the **host-neutral client
 * tier** of the data head.
 *
 * Where `./data` is the wire *contract* and `./rpc` is the machinery that lowers
 * it onto a connection, this is what a client wraps around both: the seam a host
 * fills in (`DataPort`), the connection above it (`DataConnection` — readiness
 * gate and reconnect), the participants on that connection (`DataSession` —
 * identity, open/watch ordering, echo recognition), the inbound
 * fan-out (`DataEvents`), and the two halves of the hop for hosts whose
 * client cannot hold a socket — `createPostMessageTransport` on the client
 * side and `relayToPostMessageChannel` on the side that does hold it, and
 * `createMessagePortTransport` for a head's worker `MessagePort`, at either
 * end, which the GLSP worker head uses too.
 *
 * **Neutral, and gate-enforced so.** Nothing here imports a host package or a
 * Node builtin, which is what lets one client tier serve a Theia frontend, a VS
 * Code extension host, a VS Code webview and a plain browser app. `npm run
 * check:neutral` bundles these modules for the browser; `scripts/check-neutral-bundles.mts`
 * carries the entries.
 *
 * The Theia-specific mounting of the same contract lives in
 * `@hydranium/data-client-theia`, and is now only the TRANSPORT: its
 * `ChannelDataPort` fills the `DataPort` seam over a Theia channel, and its
 * `EmitterDataClient` is the Theia-bound counterpart of `DataEvents`.
 * Everything above the port is here, so a Theia frontend and a browser page
 * differ by which port they construct and nothing else.
 */

export * from './data-connection';
export * from './data-events';
export * from './data-port';
export * from './data-session';
export * from './message-relay';
export * from './post-message-transport';
export * from './rpc-connection';
export * from './worker-handshake';
export * from './working-copy';
